import type { UseFormReturn } from 'react-hook-form';
import type { InventoryItem } from '@/lib/services/inventory-items/types';
import { LIABILITY_TYPE_SLUGS } from '@/lib/services/inventory-items/types';
import type { CustomInstitution } from './shared/InstitutionSearchField';
import { truncateText } from '@/lib/utils/data-transformers';

/**
 * Item statuses for which the facility/institution field is editable and a change
 * is sent to the backend — the active discovery workflow. A facility is frequently
 * repointed after it has been notified (the facility redirects the executor to its
 * head office / another entity), so the field must stay editable past draft. The
 * backend still blocks the change once money is tied to the asset (collected /
 * resolved / settled, or an active institutional payout) and returns a clear error
 * (LF-5319). Compared lower-cased.
 */
export const INSTITUTION_EDITABLE_STATUSES = new Set<string>([
  'draft',
  'notified',
  'identified'
]);

/**
 * Helper to get item from form state
 */
export function getFormItem(
  form: UseFormReturn<Partial<InventoryItem>>
): Partial<InventoryItem> {
  return form.getValues();
}

/**
 * Helper to safely get itemInfo with type safety
 */
export function getItemInfo(item: Partial<InventoryItem>) {
  return (item.itemInfo || {}) as Record<string, unknown>;
}

/**
 * Helper to safely get itemInfo from form state
 */
export function getFormItemInfo(form: UseFormReturn<Partial<InventoryItem>>) {
  return getItemInfo(form.getValues());
}

/**
 * Helper to get institution name from various formats
 */
export function getInstitutionName(institution: unknown): string {
  if (
    typeof institution === 'object' &&
    institution !== null &&
    'name' in institution
  ) {
    return (institution as { name: string }).name;
  }
  if (typeof institution === 'string') {
    return institution;
  }
  return '';
}

/**
 * Type guard to check if institution is a CustomInstitution.
 *
 * A freshly-added custom institution is the sentinel `{ id: -1, isCustom: true,
 * name, email }`. The `isCustom` flag is the primary marker, but it is not part
 * of every inventory form's Zod output schema, so `.parse()` strips it when the
 * value round-trips through a form (e.g. the create-matter review stage). The
 * `id === -1` sentinel always survives, so we also treat it as custom — a real
 * institution never has id -1 (the backend id is always positive). Without this,
 * a stripped `isCustom` left the sentinel to fall through as a regular
 * `institution: { id: -1 }`, which `CreateInventoryItemRequestSchema`'s
 * positive-id rule then rejected before the create even POSTed.
 */
function isCustomInstitution(inst: unknown): inst is CustomInstitution {
  return (
    !!inst &&
    typeof inst === 'object' &&
    ((inst as { isCustom?: boolean }).isCustom === true ||
      (inst as { id?: number }).id === -1)
  );
}

/**
 * Transform inventory item data for backend submission
 * Handles custom institutions by extracting name and email
 * Handles HIN shares stock objects by removing UI-only fields
 *
 * When a custom institution is selected:
 * - Removes/doesn't include institutionId
 * - Adds institutionName and institutionEmail
 * - Backend will automatically create the new institution
 *
 * For HIN shares:
 * - Removes the UI-only 'stock' field from itemInfo
 * - Keeps ticker and companyName fields for backend
 *
 * @param item - Inventory item from form
 * @returns Transformed item ready for backend submission
 */
export function prepareInventoryItemForSubmission(
  item: Partial<InventoryItem>
): Partial<InventoryItem> & {
  institutionName?: string;
  institutionEmail?: string;
  institutionType?: string;
  acknowledgedDuplicateInstitutionIds?: number[];
} {
  const prepared = { ...item };

  // Handle custom institution
  if (isCustomInstitution(item.institution)) {
    const customInst = item.institution as CustomInstitution;

    // Remove institution object
    delete prepared.institution;

    // Remove institutionId if present
    delete prepared.institutionId;

    // Add custom institution fields for backend
    return {
      ...prepared,
      institutionName: customInst.name,
      institutionEmail: customInst.email,
      /*
        Only present when the reader was actually shown candidates and chose to
        proceed (VALE-1422). Spread conditionally so an ordinary inline create sends
        no key at all — the backend DTOs use `forbidNonWhitelisted`, and an explicit
        `undefined` is noise on every other create.
      */
      ...(customInst.acknowledgedDuplicateInstitutionIds?.length
        ? {
            acknowledgedDuplicateInstitutionIds:
              customInst.acknowledgedDuplicateInstitutionIds
          }
        : {}),
      /*
        Present only when the create step actually offered the choice
        (VALE-1577) — the `Other` picker. Spread conditionally for the same
        reason as the acknowledgement above: the backend DTOs use
        `forbidNonWhitelisted`, so an explicit `undefined` is noise on every
        create that never asked.
      */
      ...(customInst.institutionType
        ? { institutionType: customInst.institutionType }
        : {})
    };
  }

  // Handle regular institution object
  if (
    item.institution &&
    typeof item.institution === 'object' &&
    'id' in item.institution
  ) {
    const inst = item.institution as { id: number };
    delete prepared.institution;
    return {
      ...prepared,
      institutionId: inst.id
    };
  }

  // Handle HIN shares - remove UI-only 'stock' field and clean up legacy fields
  if (prepared.itemInfo && typeof prepared.itemInfo === 'object') {
    const itemInfo = prepared.itemInfo as Record<string, unknown> & {
      stock?: unknown;
      portfolio?: Array<Record<string, unknown>>;
    };

    // Remove UI-only stock object from itemInfo
    if (itemInfo.stock) {
      delete itemInfo.stock;
    }

    // Clean up portfolio holdings
    if (itemInfo.portfolio && Array.isArray(itemInfo.portfolio)) {
      itemInfo.portfolio = itemInfo.portfolio.map(
        (holding: Record<string, unknown>) => {
          const { _stock, ...rest } = holding;

          // Remove tickerCompany field - frontend only sends new format
          if ('tickerCompany' in rest) {
            delete rest.tickerCompany;
          }

          return rest;
        }
      );
    }
  }

  // Return as-is if no institution or string format
  return prepared;
}

/**
 * Resolve the institution-related fields of an inventory-item UPDATE payload.
 *
 * The backend only accepts an institution change on a DRAFT item, and it accepts
 * two shapes: an existing institution as `institution: { id }`, or a new custom
 * institution as `institutionName` + `institutionEmail` (the backend then creates
 * it). A freshly added custom institution arrives as the `CustomInstitution`
 * sentinel `{ id: -1, isCustom: true, ... }`; sending `institution: { id: -1 }`
 * is rejected by `UpdateInventoryItemRequestSchema` (id must be positive) and
 * surfaces as the "Invalid inventory item update" toast (LF-4114). Routing through
 * `prepareInventoryItemForSubmission` maps the sentinel to `institutionName` /
 * `institutionEmail` so the save succeeds — matching `SimpleInventoryDrawer`.
 */
export function resolveInstitutionUpdateFields(
  updatedData: Partial<InventoryItem>,
  currentItem:
    | Pick<InventoryItem, 'status' | 'institutionId' | 'institution'>
    | undefined
): {
  /**
   * `null` is **cleared**, not absent (VALE-355). Absent means "this update says
   * nothing about the institution"; `null` is the wire's way of saying unset it,
   * which `UpdateInventoryItemRequest.institution` already accepts.
   */
  institution?: { id: number } | null;
  institutionName?: string;
  institutionEmail?: string;
  /** See {@link CustomInstitution.institutionType} (VALE-1577). */
  institutionType?: string;
  /** See {@link CustomInstitution.acknowledgedDuplicateInstitutionIds} (VALE-1422). */
  acknowledgedDuplicateInstitutionIds?: number[];
} {
  // A facility/institution change is accepted throughout the active workflow
  // (draft → notified → identified) — a notified facility often redirects the
  // executor elsewhere ("contact our head office") and the lawyer must repoint the
  // asset. The backend blocks the change only once money is tied to the asset
  // (collected/resolved/settled, or an active payout) and returns a clear error,
  // so the FE sends it for the editable statuses and lets the backend own the
  // money-safety block (LF-5319). Previously this dropped it for any non-draft
  // item, silently discarding the user's change.
  const status = currentItem?.status?.toLowerCase();
  if (!status || !INSTITUTION_EDITABLE_STATUSES.has(status)) {
    return {};
  }

  // Institution-grouped drawer items (e.g. aged-care) only carry the nested
  // `institution.id` — `institutionId` is not flattened onto the item — so fall
  // back to it (matching the same fallback in inventory-items/hooks.tsx).
  const currentInstitutionId =
    currentItem?.institutionId ?? currentItem?.institution?.id;

  const prepared = prepareInventoryItemForSubmission(updatedData);
  const newInstitutionId = prepared.institution?.id || prepared.institutionId;
  const hasInstitutionChanged = newInstitutionId !== currentInstitutionId;
  const hasNewCustomInstitution = Boolean(
    prepared.institutionName && prepared.institutionEmail
  );

  if (!hasInstitutionChanged && !hasNewCustomInstitution) {
    return {};
  }

  const fields: {
    institution?: { id: number } | null;
    institutionName?: string;
    institutionEmail?: string;
    institutionType?: string;
    acknowledgedDuplicateInstitutionIds?: number[];
  } = {};
  if (newInstitutionId) {
    fields.institution = { id: newInstitutionId };
  } else if (!hasNewCustomInstitution) {
    /*
      **The clear (VALE-355).** Reaching here with no new id and no new custom
      institution means the change *is* the removal — the reader hit the `×` on the
      institution row. This used to add no field at all, so the function returned
      `{}`, and every caller reads an empty object as "nothing to send": the clear
      button rendered, was clickable, and silently did nothing.

      `null` rather than omitted, because those mean opposite things on the wire.
    */
    fields.institution = null;
  }
  if (prepared.institutionName) {
    fields.institutionName = prepared.institutionName;
  }
  if (prepared.institutionEmail) {
    fields.institutionEmail = prepared.institutionEmail;
  }
  /*
    The reader's type choice, which only exists alongside a new custom institution
    (VALE-1577). Dropped here, the edit path would file every organisation as
    `other` while the add path honoured the choice — the same surface disagreeing
    with itself depending on which drawer reached it.
  */
  if (prepared.institutionType) {
    fields.institutionType = prepared.institutionType;
  }
  /*
    The reader's "add anyway" answer, which only exists alongside a new custom
    institution (VALE-1422). Without it the write comes back 409 again and the reader
    is stuck in a loop they have already answered their way out of.
  */
  if (prepared.acknowledgedDuplicateInstitutionIds?.length) {
    fields.acknowledgedDuplicateInstitutionIds =
      prepared.acknowledgedDuplicateInstitutionIds;
  }
  return fields;
}

/**
 * Generate the title for a HIN shares inventory item card.
 *
 * Shows the security / company name only — the HIN is deliberately omitted, as
 * it is already rendered as its own field inside the card and repeating it in
 * the title is redundant. The HIN is used as the title only as a last resort,
 * when no security/company name is available to identify the holding.
 *
 * @param itemInfo - The itemInfo object from HIN shares inventory item
 * @returns Generated title string
 */
export function generateHinSharesDescription(
  itemInfo: Record<string, unknown>
): string {
  const hin = itemInfo.hin as string | undefined;
  const ticker = itemInfo.ticker as string | undefined;
  const companyName = itemInfo.companyName as string | undefined;
  const numberOfShares = itemInfo.numberOfShares as number | undefined;
  const tickerCompany = itemInfo.tickerCompany as string | undefined;

  // Also check portfolio array for holdings data (current backend format)
  const portfolio = itemInfo.portfolio as
    | Array<Record<string, unknown>>
    | undefined;
  const holding = portfolio?.[0];

  // Get values from holding if not at top level
  const effectiveTicker = ticker || (holding?.ticker as string | undefined);
  const effectiveCompanyName =
    companyName || (holding?.companyName as string | undefined);
  const effectiveNumberOfShares =
    numberOfShares || (holding?.numberOfShares as number | undefined);
  const effectiveTickerCompany =
    tickerCompany || (holding?.tickerCompany as string | undefined);

  // Best case: ticker + company name
  if (effectiveTicker && effectiveCompanyName) {
    return truncateText(`${effectiveTicker} - ${effectiveCompanyName}`);
  }

  // Good case: ticker (with share count when available)
  if (effectiveTicker) {
    if (effectiveNumberOfShares) {
      return truncateText(
        `${effectiveTicker} - ${effectiveNumberOfShares.toLocaleString()} shares`
      );
    }
    return effectiveTicker;
  }

  // Backward compatibility: tickerCompany
  if (effectiveTickerCompany) {
    if (effectiveNumberOfShares) {
      return truncateText(
        `${effectiveTickerCompany} - ${effectiveNumberOfShares.toLocaleString()} shares`
      );
    }
    return truncateText(effectiveTickerCompany);
  }

  // Last resort: with no security/company name to show, the HIN is the only
  // identifier available (not redundant here — there is no name to repeat).
  if (hin) {
    return `HIN: ${hin}`;
  }

  // Default fallback
  return 'HIN Shares';
}

/**
 * Shared ownership type options for mortgage and vehicle loan forms.
 */
export const OWNERSHIP_TYPE_OPTIONS = [
  { value: 'solely-held', label: 'Solely Owned' },
  { value: 'joint-tenants', label: 'Joint Tenants' },
  { value: 'tenants-in-common', label: 'Tenants in Common' }
] as const;

/**
 * Shared repayment frequency options for mortgage forms.
 */
export const REPAYMENT_FREQUENCY_OPTIONS = [
  { value: 'weekly', label: 'Weekly' },
  { value: 'fortnightly', label: 'Fortnightly' },
  { value: 'monthly', label: 'Monthly' }
] as const;

/**
 * Shared encumbrance type options for mortgage forms.
 */
export const ENCUMBRANCE_TYPE_OPTIONS = [
  { value: 'first-mortgage', label: 'First Mortgage' },
  { value: 'second-mortgage', label: 'Second Mortgage' },
  { value: 'charge', label: 'Charge' },
  { value: 'lien', label: 'Lien' }
] as const;

/**
 * Shared resolution plan options for mortgage and vehicle loan forms.
 */
export const RESOLUTION_PLAN_OPTIONS = [
  { value: 'property-sale', label: 'Property Sale' },
  { value: 'beneficiary-refinances', label: 'Beneficiary Refinances' },
  { value: 'joint-tenant-assumes', label: 'Joint Tenant Assumes' },
  { value: 'estate-pays', label: 'Estate Pays' },
  { value: 'undecided', label: 'Undecided' }
] as const;

/**
 * The current-value figure a confirmation vouches for (LF-4356). FE mirror of
 * the backend `deriveConfirmableCurrentValue` — liabilities use
 * `currentBalance ?? amountOwed`; assets use `currentValue ?? currentBalance`,
 * falling back to the summed portfolio current/DoD value for portfolio types
 * (crypto / HIN shares). Returns null when there is no current value to confirm.
 */
export function deriveConfirmableCurrentValue(
  item: Partial<InventoryItem>
): number | null {
  const itemInfo = (item.itemInfo || null) as Record<string, unknown> | null;
  if (!itemInfo) return null;

  const typeSlug = typeof item.type === 'string' ? item.type : item.type?.slug;
  const isLiability =
    item.category === 'liability' ||
    (!!typeSlug && LIABILITY_TYPE_SLUGS.has(typeSlug));

  if (isLiability) {
    if (typeof itemInfo.currentBalance === 'number')
      return itemInfo.currentBalance;
    if (typeof itemInfo.amountOwed === 'number') return itemInfo.amountOwed;
    return null;
  }

  if (typeof itemInfo.currentValue === 'number') return itemInfo.currentValue;
  if (typeof itemInfo.currentBalance === 'number')
    return itemInfo.currentBalance;

  const portfolio = itemInfo.portfolio;
  if (Array.isArray(portfolio) && portfolio.length > 0) {
    let sum = 0;
    let found = false;
    for (const holding of portfolio) {
      if (typeof holding !== 'object' || holding === null) continue;
      const record = holding as Record<string, unknown>;
      const hv = record.currentValue;
      const hd = record.valueAtDateOfDeath;
      if (typeof hv === 'number') {
        sum += hv;
        found = true;
      } else if (typeof hd === 'number') {
        sum += hd;
        found = true;
      }
    }
    if (found) return sum;
  }
  return null;
}

/**
 * The date-of-death value figure a confirmation vouches for (LF-5546). FE
 * mirror of the current-value derivation above, for the LEFT field of the
 * value pair: liabilities use `amountOwed`; assets use `valueAtDateOfDeath`,
 * falling back to the summed portfolio DoD value for portfolio types (crypto /
 * HIN shares). Returns null when there is no DoD value to confirm.
 */
export function deriveConfirmableDodValue(
  item: Partial<InventoryItem>
): number | null {
  const itemInfo = (item.itemInfo || null) as Record<string, unknown> | null;
  if (!itemInfo) return null;

  const typeSlug = typeof item.type === 'string' ? item.type : item.type?.slug;
  const isLiability =
    item.category === 'liability' ||
    (!!typeSlug && LIABILITY_TYPE_SLUGS.has(typeSlug));

  if (isLiability) {
    if (typeof itemInfo.amountOwed === 'number') return itemInfo.amountOwed;
    return null;
  }

  if (typeof itemInfo.valueAtDateOfDeath === 'number')
    return itemInfo.valueAtDateOfDeath;

  const portfolio = itemInfo.portfolio;
  if (Array.isArray(portfolio) && portfolio.length > 0) {
    let sum = 0;
    let found = false;
    for (const holding of portfolio) {
      if (typeof holding !== 'object' || holding === null) continue;
      const record = holding as Record<string, unknown>;
      const hd = record.valueAtDateOfDeath;
      if (typeof hd === 'number') {
        sum += hd;
        found = true;
      }
    }
    if (found) return sum;
  }
  return null;
}

/**
 * Look up the display label for a select option value.
 * Returns the matching label, the raw value as fallback, or undefined if no value.
 */
export function formatSelectLabel(
  value: string | undefined,
  options: ReadonlyArray<{ readonly value: string; readonly label: string }>
): string | undefined {
  if (!value) return undefined;
  return options.find((o) => o.value === value)?.label || value;
}
